import { useEffect, useRef, useCallback } from 'react'
import { useUserStore } from '../store/useUserStore'
import { useExerciseStore } from '../store/useExerciseStore'
import { useIntentionStore } from '../store/useIntentionStore'
import { useYearReviewStore } from '../store/useYearReviewStore'
import { getJourney, debouncedSaveJourney, flushPendingSave } from '../services/api'
import type { YearReviewData, PhotoMemoriesData } from '../types'

/**
 * Hook to sync local store data with D1 database
 * - Loads data from server on mount (if user is logged in)
 * - Subscribes to store changes and syncs to server
 * - Handles beforeunload to flush pending saves
 */
export function useDataSync() {
  const { userId, currentYear, isLoggedIn } = useUserStore()
  const hasLoadedFromServer = useRef(false)
  const isInitialLoad = useRef(true)

  // Load data from server when user logs in
  const loadFromServer = useCallback(async () => {
    if (!userId || hasLoadedFromServer.current) return

    const journey = await getJourney(userId, currentYear)

    if (journey) {
      // Load exercise data
      useExerciseStore.getState().loadFromServer({
        horoscope: journey.horoscope,
        words: journey.words,
        vision: journey.vision,
        photos: journey.photos as PhotoMemoriesData | null,
        completedExercises: journey.completedExercises,
      })

      // Load intention data
      useIntentionStore.getState().loadFromServer({
        intentions: journey.intentions,
        actionPlans: journey.actionPlans,
        summaryCard: journey.summaryCard,
      })

      // Load year review data
      useYearReviewStore.getState().loadFromServer(journey.yearReview as YearReviewData | null)
    }

    hasLoadedFromServer.current = true
    isInitialLoad.current = false
  }, [userId, currentYear])

  // Sync current state to server
  const syncToServer = useCallback(() => {
    if (!userId || isInitialLoad.current) return

    const exerciseSnapshot = useExerciseStore.getState().getSnapshot()
    const intentionSnapshot = useIntentionStore.getState().getSnapshot()
    const yearReviewSnapshot = useYearReviewStore.getState().getSnapshot()

    debouncedSaveJourney(userId, currentYear, {
      ...exerciseSnapshot,
      ...intentionSnapshot,
      yearReview: yearReviewSnapshot,
    })
  }, [userId, currentYear])

  // Load data on mount if user is logged in
  useEffect(() => {
    if (isLoggedIn && userId) {
      loadFromServer()
    }
  }, [isLoggedIn, userId, loadFromServer])

  // Subscribe to exercise store changes
  useEffect(() => {
    if (!userId) return

    const unsubscribe = useExerciseStore.subscribe(() => {
      syncToServer()
    })

    return unsubscribe
  }, [userId, syncToServer])

  // Subscribe to intention store changes
  useEffect(() => {
    if (!userId) return

    const unsubscribe = useIntentionStore.subscribe(() => {
      syncToServer()
    })

    return unsubscribe
  }, [userId, syncToServer])

  // Subscribe to year review store changes
  useEffect(() => {
    if (!userId) return

    const unsubscribe = useYearReviewStore.subscribe(() => {
      syncToServer()
    })

    return unsubscribe
  }, [userId, syncToServer])

  // Flush pending saves on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      flushPendingSave()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      flushPendingSave()
    }
  }, [])

  return {
    loadFromServer,
    syncToServer,
  }
}

/**
 * Load past year's journey data for review
 */
export async function loadPastYearJourney(userId: number, year: number) {
  return await getJourney(userId, year)
}
